import type { RuleDefinition } from '@/types';

/** CRUD against the package's /rules endpoints (backed by the welcome MySQL). */

export class RulesApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'RulesApiError';
  }
}

async function req<T>(
  api: string,
  token: string | null,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const base = api.replace(/\/+$/, '');
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new RulesApiError(`Could not reach the rules API at ${base}.`, 0);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    // 501 = the panel has no welcome DB configured; make that legible.
    const msg =
      res.status === 501
        ? 'Rule storage is not configured on this panel (no welcome database).'
        : (body?.error ?? `Request failed (${res.status})`);
    throw new RulesApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

export function fetchRules(p: { api: string; token: string | null }): Promise<RuleDefinition[]> {
  return req<{ rules: RuleDefinition[] }>(p.api, p.token, '/rules').then((r) => r.rules);
}

export function saveRule(p: {
  api: string;
  token: string | null;
  rule: RuleDefinition;
}): Promise<RuleDefinition> {
  return req<{ rule: RuleDefinition }>(p.api, p.token, '/rules', {
    method: 'POST',
    body: JSON.stringify(p.rule),
  }).then((r) => r.rule);
}

export function deleteRule(p: {
  api: string;
  token: string | null;
  id: string;
}): Promise<boolean> {
  return req<{ removed: boolean }>(p.api, p.token, `/rules/${encodeURIComponent(p.id)}`, {
    method: 'DELETE',
  }).then((r) => r.removed);
}
